import { Component, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';
import { Subscription }                                    from 'rxjs';
import { TranslateService }                                from '@ngx-translate/core';

import { ChartCsvNamer }                from '../shared/components/charts/chart-csv-namer';
import { CompanyService }               from '../companies/company.service';
import { IntelligenceFormatService }    from './intelligence.format.service';
import { IntelligenceNarrativeService } from './intelligence.narrative.service';
import { IntelligenceRangeModel }       from './typings/intelligence.range';
import { IntelligenceService }          from './intelligence.service';
import { SessionService }               from '../sessions/session.service';
import { TabModel }                     from '../shared/components/tab-bar/typings/tab';

const EMAIL_PATTERN = /^\S+@\S+\.\S+$/;

// print rules hang off a body class while printing; @page cannot be scoped, so it is injected
const PRINT_CLASS    = 'intelligence-printing';
const PRINT_PAGE_CSS = '@page { size: A4; margin: 6mm 0; }';
const PRINT_STYLE_ID = 'intelligence-print-page';

// display order
const TAB_KEYS = [ 'overview', 'money', 'customers', 'vehicles', 'products', 'purchasing', 'mechanics', 'operations', 'opportunities' ];

@Component({
  encapsulation: ViewEncapsulation.None,
  providers    : [ { provide: ChartCsvNamer, useExisting: IntelligenceFormatService } ],
  selector     : 'wor-intelligence',
  styles: [ require('./intelligence.component.scss') ],
  template: require('./intelligence.component.html')
})
export class IntelligenceComponent implements OnInit, OnDestroy {
  public activeTab          : string  = 'overview';
  public emailError         : string  = '';
  public emailRecipient     : string  = '';
  public emailSent          : string  = '';
  public isEmailOpen        : boolean = false;
  public isEmailSending     : boolean = false;
  public isEntitled         : boolean = false;
  public isNarrativeLoading : boolean = false;
  public narrative          : string  = '';
  public narrativeError     : string  = '';

  public tabs : Array<TabModel> = [];

  private narrativeGeneration : number = 0;
  private subscription        : Subscription;

  private readonly onAfterPrint  = () : void => this.endPrint();
  private readonly onBeforePrint = () : void => this.beginPrint();

  constructor (
    private companyService              : CompanyService,
    private intelligenceFormatService   : IntelligenceFormatService,
    private intelligenceNarrativeService: IntelligenceNarrativeService,
    public intelligenceService          : IntelligenceService,
    private sessionService              : SessionService,
    private translateService            : TranslateService
  ) {
    const keys = this.companyService.hasDealership()
      ? TAB_KEYS.concat('dealership')
      : TAB_KEYS;

    this.isEntitled = this.sessionService.isAdminUser() && this.companyService.hasIntelligence();

    this.tabs = keys.map(key => ({
      label: this.translateService.instant(`INTELLIGENCE_SPACE.TABS.${ key.toUpperCase() }`),
      value: key
    }));
  }

  ngOnInit () : void {
    // a summary or email panel belongs to one tab, period and filter set, so any change retires it
    this.subscription = this.intelligenceService.rangeChanged.subscribe(() => this.clearTabState());
    this.subscription.add(this.intelligenceService.filtersChanged.subscribe(() => this.clearNarrative()));

    window.addEventListener('beforeprint', this.onBeforePrint);
    window.addEventListener('afterprint', this.onAfterPrint);
  }

  ngOnDestroy () : void {
    this.subscription.unsubscribe();

    window.removeEventListener('beforeprint', this.onBeforePrint);
    window.removeEventListener('afterprint', this.onAfterPrint);

    this.endPrint();
  }

  earliestDateLabel () : string {
    return this.intelligenceFormatService.fullDate(this.intelligenceService.earliestDate);
  }

  loadNarrative () : void {
    if ( this.isNarrativeLoading ) {
      return;
    }

    if ( this.narrative || this.narrativeError ) {
      this.clearNarrative();

      return;
    }

    const generation = ++this.narrativeGeneration;
    const tab = this.activeTab;

    this.isNarrativeLoading = true;

    this.intelligenceNarrativeService.summariseTab(tab)
    .then(narrative => {
      if ( generation !== this.narrativeGeneration ) {
        return;
      }

      // out of AI allowance: say so and offer the upgrade, rather than report a failed summary
      if ( narrative.limitReached ) {
        this.narrativeError = this.translateService.instant(narrative.upgradeAvailable
          ? 'INTELLIGENCE_SPACE.ASK.LIMIT_UPGRADE'
          : 'INTELLIGENCE_SPACE.ASK.LIMIT_REACHED');

        return;
      }

      this.narrative = narrative.text;

      if ( !narrative.text ) {
        this.narrativeError = this.translateService.instant('INTELLIGENCE_SPACE.OVERVIEW.NARRATIVE_EMPTY');
      }
    })
    .catch(() => {
      if ( generation === this.narrativeGeneration ) {
        this.narrativeError = this.translateService.instant('INTELLIGENCE_SPACE.OVERVIEW.NARRATIVE_ERROR');
      }
    })
    .finally(() => {
      if ( generation === this.narrativeGeneration ) {
        this.isNarrativeLoading = false;
      }
    });
  }

  onRangeChange ( range : IntelligenceRangeModel ) : void {
    this.intelligenceService.setRange(range);
  }

  print () : void {
    window.print();
  }

  // the range as the header reads it, one date when the range is a single day
  get rangeLabel () : string {
    const range = this.intelligenceService.range;
    const start = this.intelligenceFormatService.fullDate(range.start_date);
    const end   = this.intelligenceFormatService.fullDate(range.end_date);

    return range.start_date === range.end_date
      ? start
      : `${ start } – ${ end }`;
  }

  selectTab ( tab : string ) : void {
    this.activeTab = tab;

    // The figures behind the summary and the email panel belong to the previous tab.
    this.clearTabState();
  }

  sendEmail () : void {
    if ( this.isEmailSending ) {
      return;
    }

    if ( !EMAIL_PATTERN.test(this.emailRecipient.trim()) ) {
      this.emailError = this.translateService.instant('INTELLIGENCE_SPACE.EMAIL.INVALID');

      return;
    }

    const recipient = this.emailRecipient.trim();

    this.emailError = '';
    this.emailSent = '';
    this.isEmailSending = true;

    this.intelligenceService.emailReport(this.activeTab, recipient)
    .then(() => {
      this.emailRecipient = '';
      this.emailSent = recipient;
    })
    .catch(() => {
      this.emailError = this.translateService.instant('INTELLIGENCE_SPACE.EMAIL.ERROR');
    })
    .finally(() => {
      this.isEmailSending = false;
    });
  }

  toggleEmail () : void {
    this.isEmailOpen = !this.isEmailOpen;

    if ( !this.isEmailOpen ) {
      this.resetEmail();
    }
  }

  private beginPrint () : void {
    document.body.classList.add(PRINT_CLASS);

    if ( !document.getElementById(PRINT_STYLE_ID) ) {
      const style = document.createElement('style');

      style.id = PRINT_STYLE_ID;
      style.textContent = PRINT_PAGE_CSS;

      document.head.appendChild(style);
    }
  }

  private clearNarrative () : void {
    this.narrativeGeneration += 1;
    this.isNarrativeLoading = false;
    this.narrative = '';
    this.narrativeError = '';
  }

  private clearTabState () : void {
    this.clearNarrative();

    this.isEmailOpen = false;

    this.resetEmail();
  }

  private endPrint () : void {
    const style = document.getElementById(PRINT_STYLE_ID);

    document.body.classList.remove(PRINT_CLASS);

    if ( style ) {
      style.remove();
    }
  }

  private resetEmail () : void {
    this.emailError = '';
    this.emailRecipient = '';
    this.emailSent = '';
    this.isEmailSending = false;
  }
}
